import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, ShoppingBag, Star, TrendingUp, Users, ChevronDown, ChevronUp, Anchor, ThumbsUp, ThumbsDown, Sparkles, ExternalLink } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

const CATEGORY_LABEL = {
  gastronomía: 'Gastronomía',
  moda: 'Moda',
  salud_belleza: 'Salud y Belleza',
  entretenimiento: 'Entretenimiento',
  servicios: 'Servicios',
  ancla_retail: 'Ancla Retail',
  electrónica_hogar: 'Electrónica / Hogar',
  cultura_hobby: 'Cultura / Hobby',
  otros: 'Otros',
};

const CATEGORY_COLOR = {
  gastronomía: '#b45309',
  moda: '#7c3aed',
  salud_belleza: '#be185d',
  entretenimiento: '#0891b2',
  servicios: '#0f766e',
  ancla_retail: '#1e3a8a',
  electrónica_hogar: '#0369a1',
  cultura_hobby: '#65a30d',
  otros: '#64748b',
};

export default function PlaceInsights({ placeId, lat, lon, industry, isPlaza }) {
  const [directory, setDirectory] = useState(null);
  const [plazaIdx, setPlazaIdx] = useState(null);
  const [popularity, setPopularity] = useState(null);
  const [footTraffic, setFootTraffic] = useState(null);
  const [loadingDir, setLoadingDir] = useState(false);
  const [loadingIdx, setLoadingIdx] = useState(false);
  const [loadingPop, setLoadingPop] = useState(false);
  const [loadingFt, setLoadingFt] = useState(false);
  const [expandedCat, setExpandedCat] = useState({});

  useEffect(() => {
    if (!placeId || !lat) return;
    (async () => {
      // Foot traffic + Popularity (independent of directory)
      setLoadingFt(true);
      api.get(`/places/${placeId}/foot-traffic`)
        .then(r => setFootTraffic(r.data))
        .catch(() => toast.error('Error estimando foot-traffic'))
        .finally(() => setLoadingFt(false));

      setLoadingPop(true);
      api.get(`/places/${placeId}/popularity`, { params: { industry: industry || '' } })
        .then(r => setPopularity(r.data))
        .catch(() => toast.error('Error analizando popularidad'))
        .finally(() => setLoadingPop(false));

      // Directory + Plaza indices — only if plaza-like
      if (isPlaza) {
        setLoadingDir(true);
        setLoadingIdx(true);
        try {
          const d = await api.get(`/places/${placeId}/directory`, { params: { lat, lon, radius_m: 180 } });
          setDirectory(d.data);
          const i = await api.get(`/places/${placeId}/plaza-indices`, { params: { industry: industry || '', lat, lon } });
          setPlazaIdx(i.data);
        } catch { toast.error('Error cargando directorio de la plaza'); }
        finally { setLoadingDir(false); setLoadingIdx(false); }
      }
    })();
  }, [placeId, lat, lon, industry, isPlaza]);

  return (
    <div className="mt-6" data-testid="place-insights">
      <Tabs defaultValue="popularity" className="w-full">
        <TabsList className="w-full grid grid-cols-4 mb-4 bg-slate-100">
          <TabsTrigger value="popularity" data-testid="tab-popularity"><Sparkles size={14} className="mr-1.5" />Popularidad</TabsTrigger>
          <TabsTrigger value="foot-traffic" data-testid="tab-foot-traffic"><Users size={14} className="mr-1.5" />Foot traffic</TabsTrigger>
          <TabsTrigger value="directory" disabled={!isPlaza} data-testid="tab-directory"><ShoppingBag size={14} className="mr-1.5" />Directorio</TabsTrigger>
          <TabsTrigger value="indices" disabled={!isPlaza} data-testid="tab-indices"><TrendingUp size={14} className="mr-1.5" />Índices Plaza</TabsTrigger>
        </TabsList>

        {/* POPULARITY */}
        <TabsContent value="popularity" className="mt-0">
          <div className="bg-white border border-slate-200 p-6" data-testid="popularity-panel">
            {loadingPop ? (
              <div className="text-slate-500 flex items-center gap-2 py-8"><Loader2 size={16} className="animate-spin" />Analizando reseñas con Claude…</div>
            ) : popularity ? (
              <>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="label-caps">Popularidad digital</div>
                    <div className="font-display text-4xl font-bold text-slate-900 mt-1">{popularity.digital_score}<span className="text-lg text-slate-400">/100</span></div>
                    <div className="text-sm text-slate-600 mt-1">{popularity.digital_category}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Rating: <strong>{popularity.rating?.toFixed(1)}</strong> · Reseñas: <strong>{popularity.reviews_count?.toLocaleString('es-MX')}</strong></div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <BarKpi label="Calidad (rating)" value={popularity.components.rating_component} color="#f59e0b" />
                  <BarKpi label="Volumen (reseñas)" value={popularity.components.volume_component} color="#7c3aed" />
                  <BarKpi label="Sentimiento (LLM)" value={popularity.components.sentiment_component || 0} color="#0891b2" />
                </div>
                {popularity.review_analysis?.key_praises?.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                    <div>
                      <div className="label-caps flex items-center gap-1 text-green-800"><ThumbsUp size={12} /> Elogios frecuentes</div>
                      <ul className="mt-2 space-y-1 text-sm text-slate-700 list-disc pl-5">
                        {popularity.review_analysis.key_praises.map((p, i) => <li key={i}>{p}</li>)}
                      </ul>
                    </div>
                    <div>
                      <div className="label-caps flex items-center gap-1 text-red-800"><ThumbsDown size={12} /> Quejas recurrentes</div>
                      <ul className="mt-2 space-y-1 text-sm text-slate-700 list-disc pl-5">
                        {popularity.review_analysis.key_complaints?.map((p, i) => <li key={i}>{p}</li>) || <li className="text-slate-400">Sin quejas destacadas.</li>}
                      </ul>
                    </div>
                  </div>
                )}
                {popularity.review_analysis?.positive_themes?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-2">
                    {popularity.review_analysis.positive_themes.map((t, i) => (
                      <span key={i} className="text-xs px-2 py-1 bg-green-50 text-green-800">✓ {t}</span>
                    ))}
                    {popularity.review_analysis.negative_themes?.map((t, i) => (
                      <span key={`n-${i}`} className="text-xs px-2 py-1 bg-red-50 text-red-800">⚠ {t}</span>
                    ))}
                  </div>
                )}
              </>
            ) : <div className="text-slate-400 text-sm py-4">Sin datos.</div>}
          </div>
        </TabsContent>

        {/* FOOT TRAFFIC */}
        <TabsContent value="foot-traffic" className="mt-0">
          <div className="bg-white border border-slate-200 p-6" data-testid="foot-traffic-panel">
            {loadingFt ? (
              <div className="text-slate-500 flex items-center gap-2 py-8"><Loader2 size={16} className="animate-spin" />Estimando visitantes…</div>
            ) : footTraffic ? (
              <>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="label-caps">Estimación de visitantes (heurística)</div>
                    <div className="font-display text-3xl font-bold text-slate-900 mt-1">{footTraffic.daily_visitors.mid.toLocaleString('es-MX')}<span className="text-lg text-slate-400"> / día</span></div>
                    <div className="text-xs text-slate-500 mt-1">
                      Rango: <strong>{footTraffic.daily_visitors.low.toLocaleString('es-MX')}</strong> – <strong>{footTraffic.daily_visitors.high.toLocaleString('es-MX')}</strong> visitantes/día · Confianza: <strong>{(footTraffic.confidence * 100).toFixed(0)}%</strong>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="p-3 bg-slate-50 border border-slate-100">
                    <div className="label-caps">Semanal (mid)</div>
                    <div className="font-display text-xl font-bold text-slate-900 mt-1">{footTraffic.weekly_visitors.mid.toLocaleString('es-MX')}</div>
                  </div>
                  <div className="p-3 bg-slate-50 border border-slate-100">
                    <div className="label-caps">Mensual (mid)</div>
                    <div className="font-display text-xl font-bold text-slate-900 mt-1">{footTraffic.monthly_visitors.mid.toLocaleString('es-MX')}</div>
                  </div>
                  <div className="p-3 bg-slate-50 border border-slate-100">
                    <div className="label-caps">Anual (mid, aprox)</div>
                    <div className="font-display text-xl font-bold text-slate-900 mt-1">{(footTraffic.monthly_visitors.mid * 12).toLocaleString('es-MX')}</div>
                  </div>
                </div>
                <details className="mt-3 text-xs text-slate-600" data-testid="foot-traffic-breakdown">
                  <summary className="cursor-pointer text-slate-700 font-semibold">Ver desglose metodológico</summary>
                  <div className="mt-2 pl-3 space-y-1 border-l-2 border-slate-200">
                    <div>Baseline categoría (<code>{footTraffic.breakdown.primary_type}</code>): {footTraffic.breakdown.baseline_category} visitantes/día</div>
                    <div>Señal por reseñas: {footTraffic.breakdown.review_signal_daily.toLocaleString('es-MX')} visitantes/día ({footTraffic.breakdown.reviews_used.toLocaleString('es-MX')} reseñas usadas)</div>
                    <div>Multiplicador calificación: ×{footTraffic.breakdown.rating_multiplier}</div>
                    <div>Multiplicador horario ({footTraffic.breakdown.hours_per_week_estimate}h/sem): ×{footTraffic.breakdown.hours_multiplier}</div>
                    <div>Multiplicador movilidad (transporte): ×{footTraffic.breakdown.mobility_multiplier}</div>
                  </div>
                </details>
                <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500 italic">
                  {footTraffic.notes.map((n, i) => <div key={i}>• {n}</div>)}
                </div>
              </>
            ) : <div className="text-slate-400 text-sm py-4">Sin datos.</div>}
          </div>
        </TabsContent>

        {/* DIRECTORY */}
        <TabsContent value="directory" className="mt-0">
          <div className="bg-white border border-slate-200 p-6" data-testid="directory-panel">
            {!isPlaza ? (
              <div className="text-slate-400 text-sm py-4">Solo aplica a plazas comerciales / centros comerciales.</div>
            ) : loadingDir ? (
              <div className="text-slate-500 flex items-center gap-2 py-8"><Loader2 size={16} className="animate-spin" />Explorando tenants…</div>
            ) : directory ? (
              <>
                <div className="grid grid-cols-4 gap-3 mb-5">
                  <MetricKpi label="Tenants totales" value={directory.count} />
                  <MetricKpi label="Activos" value={directory.active} />
                  <MetricKpi label="Ocupación" value={directory.occupation_rate_pct != null ? directory.occupation_rate_pct + '%' : '—'} />
                  <MetricKpi label="Anclas" value={directory.anchors.length} />
                </div>
                {directory.anchors.length > 0 && (
                  <div className="mb-4">
                    <div className="label-caps flex items-center gap-1 mb-2"><Anchor size={12} /> Anclas detectadas</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {directory.anchors.map((a, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm p-2 bg-blue-50 border border-blue-100">
                          <span className="w-2 h-2 bg-blue-900 rounded-full flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-slate-900 truncate">{a.name}</div>
                            <div className="text-xs text-slate-500 truncate">{a.primary_type?.replace(/_/g, ' ')}</div>
                          </div>
                          <div className="text-xs text-slate-600 flex-shrink-0">
                            {a.rating && <span className="flex items-center gap-1"><Star size={10} className="text-amber-500" fill="currentColor" />{a.rating.toFixed(1)}</span>}
                            {a.reviews && <div className="text-slate-400">{a.reviews.toLocaleString('es-MX')}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="label-caps mb-2">Distribución por categoría</div>
                <div className="space-y-2">
                  {directory.by_category.map((bucket) => {
                    const color = CATEGORY_COLOR[bucket.category] || '#64748b';
                    const pct = (bucket.count / directory.count) * 100;
                    const isOpen = expandedCat[bucket.category];
                    return (
                      <div key={bucket.category} className="border border-slate-100">
                        <button onClick={() => setExpandedCat({ ...expandedCat, [bucket.category]: !isOpen })}
                          data-testid={`cat-${bucket.category}`}
                          className="w-full flex items-center gap-3 p-2 text-left hover:bg-slate-50">
                          <span className="w-1 h-8" style={{ backgroundColor: color }} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-900">{CATEGORY_LABEL[bucket.category] || bucket.category}</div>
                            <div className="h-1.5 bg-slate-100 mt-1 relative overflow-hidden">
                              <div className="absolute inset-y-0 left-0" style={{ width: `${pct}%`, backgroundColor: color }} />
                            </div>
                          </div>
                          <div className="text-sm font-semibold text-slate-700 flex-shrink-0">{bucket.count}</div>
                          {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                        {isOpen && (
                          <div className="p-3 pt-0 space-y-1 bg-slate-50/50">
                            {bucket.tenants.map((t, ti) => (
                              <div key={ti} className="text-xs flex items-center gap-2 py-1">
                                <span className="flex-1 truncate">{t.name}</span>
                                {t.rating && <span className="text-slate-500">★ {t.rating.toFixed(1)}</span>}
                                {t.reviews && <span className="text-slate-400">{t.reviews.toLocaleString('es-MX')}</span>}
                                {t.is_anchor && <span className="text-xs px-1.5 py-0.5 bg-blue-900 text-white font-semibold">ANCLA</span>}
                                {!t.is_active && <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-800">CERRADO</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : <div className="text-slate-400 text-sm py-4">Sin tenants detectados en el radio.</div>}
          </div>
        </TabsContent>

        {/* PLAZA INDICES */}
        <TabsContent value="indices" className="mt-0">
          <div className="bg-white border border-slate-200 p-6" data-testid="indices-panel">
            {!isPlaza ? (
              <div className="text-slate-400 text-sm py-4">Solo aplica a plazas comerciales.</div>
            ) : loadingIdx ? (
              <div className="text-slate-500 flex items-center gap-2 py-8"><Loader2 size={16} className="animate-spin" />Calculando índices…</div>
            ) : plazaIdx?.indices ? (
              <>
                <div className="flex items-baseline justify-between mb-5 pb-4 border-b border-slate-100">
                  <div>
                    <div className="label-caps">Score comercial global (para giro: <span className="text-blue-900 font-semibold">{plazaIdx.industry_category}</span>)</div>
                    <div className="font-display text-4xl font-bold text-slate-900 mt-1">{plazaIdx.global_score}<span className="text-lg text-slate-400">/100</span></div>
                    <div className="text-sm text-slate-600 mt-1">{plazaIdx.global_category}</div>
                  </div>
                  <div className="text-right text-xs text-slate-500 space-y-0.5">
                    <div>Tenants: <strong>{plazaIdx.summary.tenants}</strong></div>
                    <div>Categorías: <strong>{plazaIdx.summary.categories}</strong></div>
                    <div>Mismo giro: <strong>{plazaIdx.summary.same_industry}</strong></div>
                    <div>Reseñas totales: <strong>{plazaIdx.summary.total_reviews.toLocaleString('es-MX')}</strong></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(plazaIdx.indices).map(([key, val]) => (
                    <IndexBar key={key} indexKey={key} data={val} />
                  ))}
                </div>
              </>
            ) : <div className="text-slate-400 text-sm py-4">Sin datos suficientes.</div>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const BarKpi = ({ label, value, color }) => (
  <div className="p-3 bg-slate-50 border border-slate-100">
    <div className="label-caps">{label}</div>
    <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value != null ? Math.round(value) : '—'}</div>
    <div className="h-1.5 bg-slate-200 mt-2 relative overflow-hidden">
      <div className="absolute inset-y-0 left-0" style={{ width: `${Math.min(100, value || 0)}%`, backgroundColor: color }} />
    </div>
  </div>
);

const MetricKpi = ({ label, value }) => (
  <div className="p-3 bg-slate-50 border border-slate-100">
    <div className="label-caps">{label}</div>
    <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</div>
  </div>
);

const IndexBar = ({ indexKey, data }) => {
  const score = data.score;
  const inverted = data.inverted; // saturation: score already inverted so still "higher = better for the industry"
  const color = score >= 70 ? '#15803d' : score >= 50 ? '#0f766e' : score >= 30 ? '#b45309' : '#b91c1c';
  return (
    <div className="p-3 border border-slate-100 bg-slate-50/40" data-testid={`plaza-index-${indexKey}`}>
      <div className="flex items-baseline justify-between">
        <div className="text-sm font-semibold text-slate-900">{data.label}</div>
        <div className="text-xl font-display font-bold" style={{ color }}>{score}</div>
      </div>
      <div className="h-1.5 bg-slate-200 mt-1.5 relative overflow-hidden">
        <div className="absolute inset-y-0 left-0" style={{ width: `${Math.max(0, Math.min(100, score))}%`, backgroundColor: color }} />
      </div>
      <div className="text-xs text-slate-500 mt-1.5">{data.detail}</div>
    </div>
  );
};
